import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AssetCardComponent } from '@features/shopping/asset/card/asset-card.component';
import { ShoppingCartData } from '@models/shopping-cart.model';
import { OrderTypeSettings } from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { CustomizeService } from '@services/customize-service/customize.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { PriceSummaryComponent } from '@shared-components/price-summary/price-summary.component';
import { RestrictInputDirective } from '@shared/directives/restrict-input/restrict-input.directive';
import { StrictEmailValidatorUtils } from '@utils/strict-email-validator.utils';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'ts-activity-13-transfer-details',
  templateUrl: './transfer-details.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RestrictInputDirective,
    AssetCardComponent,
    PriceSummaryComponent,
    TranslationPipe
  ]
})
export class TransferDetailsComponent implements OnInit, OnDestroy {
  shopping_cart: ShoppingCartData;
  refresh_summary: Subject<boolean> = new Subject<boolean>();
  restrict_change_carrier?: boolean;
  transfer_details_form: FormGroup = new FormGroup(
    {
      keep_existing_carrier: new FormControl(''),
      created_for_personal_email: new FormControl('', [
        Validators.required,
        StrictEmailValidatorUtils.strictEmailValidator
      ]),
      transfer_notes: new FormControl('')
    },
    { updateOn: 'change' }
  );
  private subscriptions: Subscription = new Subscription();

  constructor(
    private customize_svc: CustomizeService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService,
    private activity_state_svc: ActivityStateService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.customize_svc.cartChanges.subscribe((res: ShoppingCartData) => {
        this.shopping_cart = { ...res };
      })
    );

    this.subscriptions.add(
      this.transfer_details_form.valueChanges.subscribe(() => {
        const keep_existing_carrier_value =
          this.transfer_details_form.get('keep_existing_carrier')?.value;
        const keep_existing_carrier = {
          value: keep_existing_carrier_value,
          ...(keep_existing_carrier_value && {
            name:
              keep_existing_carrier_value === 'Yes'
                ? this.translation_svc.translate('keep_carrier')
                : this.translation_svc.translate('switch_carrier')
          })
        };

        this.shopping_cart.transfer_of_liability_attributes = {
          keep_existing_carrier: keep_existing_carrier,
          created_for_personal_email: this.transfer_details_form.get('created_for_personal_email')
            ?.value,
          transfer_notes: this.transfer_details_form.get('transfer_notes')?.value
        };
      })
    );

    this.shopping_svc.getPlanSettings(this.activity_state_svc.orderTypeId()).subscribe({
      next: (res: OrderTypeSettings) => {
        if (!this.shopping_cart) {
          this.shopping_cart = {} as ShoppingCartData;
        }
        this.shopping_cart.order_type_settings = res;
        this.activity_state_svc.setParameter('order_type_settings', res);
        this.restrict_change_carrier = res.restrict_transfer_to_another_carrier;

        const change_carrier = this.transfer_details_form.get('keep_existing_carrier');
        if (!this.restrict_change_carrier) {
          change_carrier?.setValidators([Validators.required]);
        } else {
          change_carrier?.clearValidators();
        }
        change_carrier?.updateValueAndValidity();
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get valid_cart(): boolean {
    return Boolean(this.shopping_cart);
  }

  get disable_continue_btn(): boolean {
    return this.valid_cart && this.transfer_details_form.valid;
  }

  get navigate_page(): string {
    return `shop/${this.shopping_cart?.orderTypeId}/finalize-order`;
  }

  get created_for_personal_email() {
    return this.transfer_details_form.get('created_for_personal_email');
  }

  getShoppingCartData(cart_data: ShoppingCartData): void {
    if (cart_data) {
      this.shopping_cart = cart_data;
      if (this.shopping_cart?.transfer_of_liability_attributes) {
        this.transfer_details_form.patchValue({
          keep_existing_carrier:
            this.shopping_cart.transfer_of_liability_attributes.keep_existing_carrier?.value,
          created_for_personal_email:
            this.shopping_cart.transfer_of_liability_attributes.created_for_personal_email ?? '',
          transfer_notes: this.shopping_cart.transfer_of_liability_attributes.transfer_notes ?? ''
        });
      }
    }
  }

  onContinueSuccess(event: boolean): void {
    if (event && this.transfer_details_form.valid) {
      this.shopping_svc.sendShoppingItems({ ...this.shopping_cart }, false).subscribe({
        next: (res: ShoppingCartData) => (this.shopping_cart = res),
        error: (err: HttpErrorResponse) => {}
      });
    }
  }
}
